import { OPERATION_TYPE } from '../../operations/models/operations.models';

/**
 * Helpers de lectura de controles del editor de workflows (NIF-530).
 *
 * Abrir cuando: lees valores del FormGroup sin repetir strings
 * `operations.{TYPE}.{path}` / unwrap de `ISelect`.
 *
 * El DS Infinity a menudo guarda `{ id, label }` en lugar de un string plano;
 * estos helpers unifican ambos casos.
 */

/** Forma mínima de FormGroup para no acoplar `@angular/forms` en el util. */
type WorkflowFormLike = { get(path: string): { value: unknown } | null };

/**
 * Resuelve el id de un `tec-select`/`ISelect` o el string plano si ya viene así.
 *
 * @param value - Control value (`{ id }` o string/null)
 * @param fallback - Valor si `value` vacío o sin `id`
 * @returns Id tipado o `fallback`
 */
export function resolveSelectIdOrPlainValue<T extends string>(
	value: unknown,
	fallback: T,
): T {
	if (typeof value === 'object' && value && 'id' in value) {
		return (((value as { id: string }).id || fallback) as T);
	}
	return ((value || fallback) as T);
}

/**
 * Valor crudo de un control bajo `operations.{operationType}.{controlPath}`.
 *
 * @param form - Form del editor (o mock con `.get`)
 * @param operationType - p. ej. `OPERATION_TYPE.OCR`
 * @param controlPath - Path relativo (`tutorial`, `fraud_scoring.aeat`, …)
 */
export function readOperationControlValue(
	form: WorkflowFormLike,
	operationType: string,
	controlPath: string,
): unknown {
	return form.get(`operations.${operationType}.${controlPath}`)?.value;
}

/**
 * Como {@link readOperationControlValue} + unwrap de `ISelect.id` si aplica.
 *
 * @returns `id` del select, el valor plano, o `undefined` si no hay control
 */
export function readOperationSelectId(
	form: WorkflowFormLike,
	operationType: string,
	controlPath: string,
): unknown {
	const value = readOperationControlValue(form, operationType, controlPath);
	if (typeof value === 'object' && value && 'id' in value) {
		return (value as { id: string }).id;
	}
	return value;
}

/**
 * Atajo OCR: equivalente a `readOperationControlValue(form, OCR, relativePath)`.
 *
 * @param relativePath - Path bajo `operations.OCR` (sin el prefijo)
 */
export function readOcrFormValue(form: WorkflowFormLike, relativePath: string): unknown {
	return readOperationControlValue(form, OPERATION_TYPE.OCR, relativePath);
}

/**
 * Atajo OCR: ISelect.id o valor plano bajo `operations.OCR.*`.
 *
 * @param relativePath - Path bajo `operations.OCR`
 */
export function readOcrSelectId(form: WorkflowFormLike, relativePath: string): unknown {
	return readOperationSelectId(form, OPERATION_TYPE.OCR, relativePath);
}

/**
 * Flags OCR tipo AEAT/AML/PEP: el form guarda ISelect o string `'true'|'false'`.
 *
 * @returns `true` solo si el id/valor resuelto es exactamente `'true'`
 */
export function isOcrSelectFlagTrue(form: WorkflowFormLike, relativePath: string): boolean {
	return readOcrSelectId(form, relativePath) === 'true';
}

/**
 * Añade o quita un ítem de una lista (tests POL, connections, …) sin mutar el array original.
 * Si el estado pedido ya está aplicado, devuelve la misma referencia.
 *
 * @param list - Lista actual del form
 * @param item - Elemento a incluir/excluir
 * @param include - `true` = asegurar presencia; `false` = quitar
 */
export function toggleArrayMembership<T>(
	list: T[] | null | undefined,
	item: T,
	include: boolean,
): T[] {
	const current = Array.isArray(list) ? list : [];
	if (include) {
		return current.includes(item) ? current : current.concat(item);
	}
	return current.includes(item) ? current.filter((entry) => entry !== item) : current;
}

/**
 * ¿Todos los controles del grupo son válidos excepto `field`?
 * (misma regla legacy de `formIsValidExcept` del orquestador)
 *
 * @param controls - Mapa `controls` de un FormGroup (o parcial)
 * @param field - Nombre del control a ignorar
 */
export function areFormControlsValidExcept(
	controls: Record<string, { valid: boolean }> | null | undefined,
	field: string,
): boolean {
	if (!controls) {
		return true;
	}
	return Object.entries(controls)
		.filter(([key]) => key !== field)
		.every(([, control]) => control.valid);
}

/**
 * Marca `completed=true` en ops visibles anteriores a `targetIndex` (progreso UI; NIF-735).
 * `emitEvent: false` evita reset de selección en `valueChanges` del orquestador.
 *
 * @param form - Form del editor (o mock con `.get`)
 * @param operations - Lista visible (`operationsFiltered`)
 * @param targetIndex - Índice de la op destino (no se marca a sí misma)
 */
export function markPrecedingOperationsCompleted(
	form: {
		get(
			path: string,
		): { patchValue(value: boolean, options?: { emitEvent?: boolean }): void } | null;
	},
	operations: Array<{ type: string }>,
	targetIndex: number,
): void {
	for (let i = 0; i < targetIndex && i < operations.length; i++) {
		const type = operations[i]?.type;
		if (!type) {
			continue;
		}
		form.get(`operations.${type}.completed`)?.patchValue(true, { emitEvent: false });
	}
}

/**
 * Pone `completed=false` desde `fromIndex` inclusive (NIF-735: al añadir op, paso a paso otra vez).
 *
 * @param form - Form del editor (o mock con `.get`)
 * @param operations - Lista visible
 * @param fromIndex - Primera op a invalidar (la nueva y las siguientes)
 */
export function clearOperationsCompletedFrom(
	form: {
		get(
			path: string,
		): { patchValue(value: boolean, options?: { emitEvent?: boolean }): void } | null;
	},
	operations: Array<{ type: string }>,
	fromIndex: number,
): void {
	for (let i = Math.max(0, fromIndex); i < operations.length; i++) {
		const type = operations[i]?.type;
		if (!type) {
			continue;
		}
		form.get(`operations.${type}.completed`)?.patchValue(false, { emitEvent: false });
	}
}

/**
 * Gris/disabled del menú lateral (NIF-735): bloqueo solo paso a paso.
 * Una op queda habilitada si todas las anteriores están `completed` **o** ella misma ya lo está
 * (edición / ops ya configuradas). Al añadir una op nueva se limpia `completed` desde ella:
 * las siguientes vuelven a gris una a una.
 *
 * @param operations - Lista completa de ops (mutates `disabled`)
 * @param isCompleted - Lee el flag `completed` del form por `type`
 */
export function applyOperationsDisabledFromCompleted(
	operations: Array<{ type: string; show: boolean; disabled: boolean }>,
	isCompleted: (type: string) => boolean,
): void {
	const visible = operations.filter((opt) => opt.show);
	let precedingAllCompleted = true;
	for (const opt of visible) {
		const selfCompleted = isCompleted(opt.type);
		opt.disabled = !(precedingAllCompleted || selfCompleted);
		precedingAllCompleted = precedingAllCompleted && selfCompleted;
	}
	for (const opt of operations) {
		if (!opt.show) {
			opt.disabled = true;
		}
	}
}

/**
 * ¿Selección de operaciones inválida? (ninguna core activa y el step OPERATIONS está seleccionado)
 *
 * @param available - Flags `available` de cada op core
 * @param operationsStepSelected - `steps[1].selected`
 */
export function isWorkflowOperationsSelectionInvalid(
	available: {
		ocr: boolean;
		pol: boolean;
		form: boolean;
		documents: boolean;
		connections: boolean;
		attorneyPowers: boolean;
	},
	operationsStepSelected: boolean,
): boolean {
	return (
		!available.ocr &&
		!available.pol &&
		!available.form &&
		!available.documents &&
		!available.connections &&
		!available.attorneyPowers &&
		operationsStepSelected
	);
}
